import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n, MONTH_NAMES, DOW_NAMES, formatDateLocale } from "@/lib/i18n";
import { toISO, todayISO, buildMonthGrid } from "@/lib/calendar";
import { useMobilI18n } from "@/lib/mobilI18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MobilRequestForm from "@/components/mobil/MobilRequestForm";

function DayCell({ day, iso, count, isPast, isToday, onClick, m }) {
  const marked = count > 0;
  return (
    <button
      onClick={onClick}
      disabled={isPast}
      data-testid={`mobil-calendar-day-${iso}`}
      data-state={isPast ? "past" : marked ? "marked" : "free"}
      title={isPast ? "" : marked ? m.calendar.tooltipMarked(count) : m.calendar.tooltipFree}
      className={`relative aspect-square rounded-lg flex flex-col items-center justify-center transition-[transform,background-color,border-color]
        ${isPast ? "opacity-40 cursor-not-allowed bg-[#fdfcf9] border border-[#eae0df]/60" : "cursor-pointer hover:-translate-y-0.5"}
        ${!isPast && marked ? "bg-[#E2C9A0]/30 border border-[#C9A24B]/60 hover:bg-[#E2C9A0]/50" : ""}
        ${!isPast && !marked ? "bg-[#fdfcf9] border border-[#eae0df] hover:bg-[#E2C9A0]/20 hover:border-[#3e664c]/40" : ""}
        ${isToday ? "ring-2 ring-[#3e664c] ring-offset-1 ring-offset-white" : ""}`}
    >
      <span className="font-display text-lg md:text-xl leading-none text-[#2A2421]">{day}</span>
      {!isPast && marked && (
        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#C9A24B] text-[9px] text-white flex items-center justify-center">
          {count}
        </span>
      )}
      {!isPast && !marked && <span className="mt-1 w-1 h-1 rounded-full bg-[#6F8C76]" />}
    </button>
  );
}

export default function MobilCalendar({ whatsappNumber, refreshKey }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [counts, setCounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const { locale } = useI18n();
  const { m } = useMobilI18n();

  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    api.get("/mobile/calendar", { params: { year, month: month + 1 } })
      .then((r) => { if (active) setCounts(r.data); })
      .catch(() => { if (active) setCounts([]); });
    return () => { active = false; };
  }, [year, month, reload, refreshKey]);

  const byDate = useMemo(() => Object.fromEntries(counts.map((c) => [c.date, c.count])), [counts]);
  const today = todayISO();
  const go = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };

  return (
    <section id="disponibilitat-externs" data-testid="mobil-calendar-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F2EDE5] aura-grain">
      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }} className="lg:col-span-2 lg:sticky lg:top-28">
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-4">{m.calendar.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">{m.calendar.title}</h2>
          <p className="mt-6 text-[#5C544F] leading-relaxed max-w-md">{m.calendar.desc}</p>
          <p className="mt-6 flex items-center gap-3 text-sm text-[#3e664c]"><CalendarDays size={18} /> {m.calendar.hint}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }}
          className="lg:col-span-3 bg-white rounded-3xl border border-[#eae0df] p-5 md:p-7 shadow-[0_8px_30px_rgb(42,36,33,0.06)]" data-testid="mobil-calendar-card">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => go(-1)} data-testid="mobil-calendar-prev" aria-label="prev" className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c] hover:text-[#3e664c] transition-colors"><ChevronLeft size={16} /></button>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight" data-testid="mobil-calendar-month">
              {MONTH_NAMES[locale][month]} <span className="text-[#3e664c]">{year}</span>
            </h3>
            <button onClick={() => go(1)} data-testid="mobil-calendar-next" aria-label="next" className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c] hover:text-[#3e664c] transition-colors"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {DOW_NAMES[locale].map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-[0.18em] text-[#5C544F]/70 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {buildMonthGrid(year, month).map((day, idx) => {
              if (day === null) return <div key={`e-${idx}`} className="aspect-square" />;
              const iso = toISO(year, month, day);
              return (
                <DayCell key={iso} day={day} iso={iso} m={m} count={byDate[iso] || 0}
                  isPast={iso < today} isToday={iso === today} onClick={() => setSelected(iso)} />
              );
            })}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-5 text-[10px] uppercase tracking-[0.2em] text-[#5C544F]">
            <span className="flex items-center gap-2" data-testid="mobil-legend-free"><span className="w-3 h-3 rounded-full bg-[#fdfcf9] border border-[#eae0df] inline-flex items-center justify-center"><span className="w-1 h-1 rounded-full bg-[#6F8C76]" /></span>{m.calendar.legendFree}</span>
            <span className="flex items-center gap-2" data-testid="mobil-legend-marked"><span className="w-3 h-3 rounded-md bg-[#E2C9A0]/40 border border-[#C9A24B]/60" />{m.calendar.legendMarked}</span>
          </div>
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#fdfcf9] border-[#eae0df] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="mobil-request-dialog">
          <DialogHeader>
            <p className="uppercase tracking-[0.28em] text-[10px] text-[#3e664c] mb-1">{m.form.eyebrow}</p>
            <DialogTitle className="font-display text-3xl tracking-tight text-[#2A2421] first-letter:uppercase">{formatDateLocale(selected, locale)}</DialogTitle>
            <DialogDescription className="text-[#5C544F]">{m.form.desc}</DialogDescription>
          </DialogHeader>
          {selected && (
            <MobilRequestForm key={selected} fixedDate={selected} whatsappNumber={whatsappNumber} testPrefix="mobil-dialog"
              onDone={() => { setReload((n) => n + 1); setSelected(null); }} />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
